import { DesignTemplate } from "@/api/config/chalice-types";
import useScheduleCustomTime from "@/pages/Posts/PostCard/ScheduleCustomTime/useScheduleCustomTime";
import { Button } from "antd";
import ResponsiveModal from "components/ResponsiveModal/ResponsiveModal";
import { genericError } from "fixtures/globalConstants";
import useGetPostDetailsQuery from "hooks/useGetPostDetailsQuery";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "store";
import { Post } from "store/posts/postConstants";
import { IntegrationType } from "store/user/userConstants";
import {
  currentBusinessGetter,
  integrationsTypeGetter,
} from "store/user/userSlice";
import DesignEditor from "../DesignEditor/DesignEditor";
import DiscardChangesConfirmation from "../DiscardChangesConfirmation";
import PostEditorContent from "./components/PostEditorContent";
import PostEditorFooter from "./components/PostEditorFooter";
import PostPreview from "./components/PostPreview/PostPreview";
import usePostEditorActions from "./hooks/usePostEditorActions";
import { DROPDOWN_BUTTONS, PostEditingDetails } from "./postEditorConstants";

type PostEditorProps = {
  existingPost?: Post;
  refetchAfterSchedule?: boolean;
  closeEditor: () => void;
  isOpen: boolean;
};

const PostEditorModal = ({
  existingPost,
  refetchAfterSchedule,
  closeEditor,
  isOpen,
}: PostEditorProps) => {
  const [caption, setCaption] = useState("");
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [template, setTemplate] = useState<DesignTemplate | undefined>();
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const [showDiscardChangesModal, setShowDiscardChangesModal] = useState(false);
  const [publishTo, setPublishTo] = useState<IntegrationType[] | null>(null);

  const {
    data: existingPostDetails,
    error,
    refetch: refetchDetails,
    status: postDetailsStatus,
    isFetching: isFetchingDetails,
  } = useGetPostDetailsQuery<PostEditingDetails | undefined, false>(
    isOpen && existingPost?.id ? existingPost?.id : undefined,
    {
      enabled: isOpen && !!existingPost?.id,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      select: (
        post: Partial<Post> | undefined | null | false
      ): PostEditingDetails | undefined => {
        if (!post || !post.inputs) {
          return undefined;
        }
        const { template, media_urls, inputs } = post;
        return {
          mediaUrls: media_urls ?? [],
          template: template ?? undefined,
          inputs: inputs as PostEditingDetails["inputs"],
        };
      },
    }
  );

  useEffect(() => {
    // there's gotta be a better way to do this but without this, the state of the modal doesn't get discarded
    if (isOpen) {
      setCaption(existingPost?.caption ?? "");
      setMediaUrls(existingPost?.media_urls ?? []);
      if (!existingPost?.id) {
        setTemplate(undefined);
      }
      setPublishTo(existingPost?.publish_to ?? []);
      setIsFullScreenOpen(false);
    }
  }, [isOpen, existingPost?.id]);

  useEffect(() => {
    if (isOpen && postDetailsStatus === "success" && existingPostDetails) {
      setTemplate(existingPostDetails.template ?? undefined);
    } else if (isOpen && postDetailsStatus === "error") {
      genericError(error ?? "Failed to fetch post details.");
    }
  }, [isOpen, postDetailsStatus, existingPostDetails, error]);

  const { customTimeMenuItem, CustomTimeModal } = useScheduleCustomTime();
  const connectedSocials = useAppSelector(integrationsTypeGetter);
  const business = useAppSelector(currentBusinessGetter);
  const { website } = business;

  const dropdownButtons = useMemo(
    () =>
      DROPDOWN_BUTTONS({
        hasConnectedSocials: Boolean(connectedSocials.length),
        customTimeMenuItem,
        post: existingPost,
      }),
    [existingPost, connectedSocials.length, customTimeMenuItem]
  );

  const { loading, getSavedPost, handleAction } = usePostEditorActions({
    existingPost,
    refetch: refetchDetails,
    refetchAfterSchedule,
    close: closeEditor,
    caption,
    mediaUrls,
    template,
    publishTo: publishTo,
  });

  const isPublishedPost = existingPost?.status === "PUBLISHED";

  const openDesignEditor = () => {
    if (!isFetchingDetails) {
      setIsFullScreenOpen(true);
    } else {
      console.warn(
        "Cannot open fullscreen editor while post details are loading."
      );
    }
  };

  const onCloseFullscreen = (updatedTemplate: DesignTemplate | undefined) => {
    if (updatedTemplate) {
      console.log("PostEditorModal: Updating state from DesignEditor", {
        updatedTemplate,
      });
      const newMediaUrls = updatedTemplate.pages.map(
        (page) => page.custom?.image_url ?? ""
      );
      setTemplate(updatedTemplate);
      setMediaUrls(
        updatedTemplate.render_design_as === "VIDEO"
          ? [newMediaUrls[0]]
          : newMediaUrls
      );
    }
    setIsFullScreenOpen(false);
  };

  const confirmClose = () => {
    const originalCaption = existingPost?.caption ?? "";
    const originalMediaUrls = existingPost?.media_urls ?? [];
    const originalTemplate = existingPostDetails?.template;

    const captionChanged = caption !== originalCaption;
    const mediaChanged =
      JSON.stringify(mediaUrls.slice().sort()) !==
      JSON.stringify(originalMediaUrls.slice().sort());
    const templateChanged =
      JSON.stringify(template) !== JSON.stringify(originalTemplate);
    const publishToChanged =
      JSON.stringify(publishTo) !== JSON.stringify(existingPost?.publish_to);

    const hasUnsavedChanges =
      captionChanged || mediaChanged || templateChanged || publishToChanged;

    if (hasUnsavedChanges && !isPublishedPost) {
      setShowDiscardChangesModal(true);
    } else {
      closeEditor();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <ResponsiveModal
        open={isOpen && !isFullScreenOpen}
        title={
          isPublishedPost
            ? "Post Details"
            : existingPost
              ? "Edit Post"
              : "Create Post"
        }
        onCancel={confirmClose}
        footer={
          !isPublishedPost ? (
            <PostEditorFooter
              post={existingPost}
              mediaUrls={mediaUrls}
              disabled={!mediaUrls || mediaUrls.length === 0}
              loading={loading || isFetchingDetails}
              dropdownButtons={dropdownButtons}
              handleAction={handleAction}
              publishToState={[publishTo, setPublishTo]}
              close={closeEditor}
            />
          ) : (
            <Button type="primary" onClick={closeEditor}>
              Close
            </Button>
          )
        }
      >
        <div className="flex flex-col md:flex-row gap-4 min-h-[500px] h-full">
          <div className="flex-grow md:basis-3/5 overflow-y-auto pr-2">
            <PostEditorContent
              post={existingPost}
              captionState={[caption, setCaption]}
              mediaUrlsState={[mediaUrls, setMediaUrls]}
              templateState={[template, setTemplate]}
              openDesignEditor={openDesignEditor}
              onRefetchNeeded={refetchDetails}
            />
          </div>
          <div className="hidden md:block flex-shrink-0 md:basis-2/5 md:h-full">
            <PostPreview
              caption={caption}
              mediaUrls={mediaUrls}
              publishTo={publishTo}
              existingPost={existingPost}
              link={website ?? undefined}
              isTemplateVideo={template?.render_design_as === "VIDEO"}
            />
          </div>
        </div>
      </ResponsiveModal>

      <DesignEditor
        isOpen={isFullScreenOpen}
        closeFullscreen={onCloseFullscreen}
        template={template}
      />

      <DiscardChangesConfirmation
        confirmationOpen={showDiscardChangesModal}
        setConfirmationOpen={setShowDiscardChangesModal}
        discardChanges={closeEditor}
      />
      <CustomTimeModal
        refetchAfterSchedule={refetchAfterSchedule}
        getPostToSchedule={() => getSavedPost({ showMessage: false })}
        onCompleteSchedule={closeEditor}
      />
    </>
  );
};

export default PostEditorModal;
